import { IFavoriteProjectResponseModel } from "@lib/types";
import Clients from "core/api-clients";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// no need for the other keys since we don't cache the responses
export const FAVORITE_PROJECTS_QUERY_KEY = {
  GET_ALL: "favorite-projects",
};

export function useFavoriteProjects(userId: number, accountNumber: number) {
  return useQuery<Array<IFavoriteProjectResponseModel>>({
    queryKey: [FAVORITE_PROJECTS_QUERY_KEY.GET_ALL, userId, accountNumber],
    queryFn: async () =>
      await Clients.ManagementBackend.FavoriteProjects.get(
        userId,
        accountNumber,
      ),
  });
}

export function useCreateFavoriteProject(
  userId: number,
  accountNumber: number,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (projectId: number) =>
      await Clients.ManagementBackend.FavoriteProjects.post(
        userId,
        accountNumber,
        projectId,
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [FAVORITE_PROJECTS_QUERY_KEY.GET_ALL, userId, accountNumber],
      }),
  });
}

export function useDeleteFavoriteProject(
  userId: number,
  accountNumber: number,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (projectId: number) =>
      await Clients.ManagementBackend.FavoriteProjects.delete(
        userId,
        accountNumber,
        projectId,
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [FAVORITE_PROJECTS_QUERY_KEY.GET_ALL, userId, accountNumber],
      }),
  });
}

export function useToggleFavoriteProject(
  userId: number,
  accountNumber: number,
) {
  const createFavorite = useCreateFavoriteProject(userId, accountNumber);
  const deleteFavorite = useDeleteFavoriteProject(userId, accountNumber);

  return useMutation({
    mutationFn: async ({
      projectId,
      isFavorite,
    }: {
      projectId: number;
      isFavorite: boolean;
    }) => {
      return isFavorite
        ? await deleteFavorite.mutateAsync(projectId)
        : await createFavorite.mutateAsync(projectId);
    },
  });
}
